import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { noop } from 'lodash';
import AdManager from 'libs/common/ad-manager';
import { receivedAdEvent, viewAdEvent } from 'libs/common/event-tracker/events';
import useTracking from 'hooks/useTracking';
import useIsConsentBannerLoaded from 'hooks/useIsConsentBannerLoaded';
import useTranslate from 'hooks/useTranslate';
import { getIsCookieConsentVersion } from 'state/consent/selectors';
import { AdPlatform, AdShape } from 'constants/ads';
import AdInfo from '../AdInfo';
import useAdRenderedCheck from '../useAdRenderedCheck';
import useAdLoadtimeLogging from '../useAdLoadtimeLogging';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const RtbAdvertisement = _ref => {
  let {
    id,
    config,
    isManuallyRendered = false,
    isManuallyRefreshed = false,
    onAdRender = noop
  } = _ref;
  const placementSizeRef = useRef([0, 0]);
  const placementRegisteredRef = useRef(false);
  const {
    0: isAdInfoVisible,
    1: setIsAdInfoVisible
  } = useState(false);
  const {
    0: adDivElement,
    1: setAdDivElement
  } = useState(null);
  const isRefreshEnabled = useMemo(() => AdManager.getAdsPlatform() === AdPlatform.Web || config.shape === AdShape.Leaderboard, [config]);
  const translate = useTranslate('advertisements');
  const {
    track
  } = useTracking();
  const isCookieConsentVersion = useSelector(getIsCookieConsentVersion);
  const isConsentBannerLoaded = useIsConsentBannerLoaded();
  const {
    onRequest,
    onLoad
  } = useAdLoadtimeLogging();
  const normalizePlacementSize = useCallback(size => {
    // Advertisement size is sometimes 1x1 or similar, even though the actual rendered ad is
    // of different size.
    if (!size) return size;
    if (typeof size === 'string') return size;
    const adCreativeElement = adDivElement === null || adDivElement === void 0 ? void 0 : adDivElement.firstElementChild;
    const [width, height] = size;
    let normalizedSize = [width, height];

    if (width <= 1 && adCreativeElement instanceof HTMLElement) {
      normalizedSize = [adCreativeElement.offsetWidth, adCreativeElement.offsetHeight];
    }

    return normalizedSize;
  }, [adDivElement]);
  const handleOnImpressionViewable = useCallback(event => {
    track(viewAdEvent({
      adUnitPath: event.slot.getAdUnitPath(),
      size: placementSizeRef.current,
      shape: config.shape,
      isMobileWeb: AdManager.getAdsPlatform() === AdPlatform.Mobile
    }));
  }, [track, config]);
  const handleOnPlacementRenderEnded = useCallback(event => {
    if (event.isEmpty || !event.size) return;
    placementSizeRef.current = normalizePlacementSize(event.size);
  }, [normalizePlacementSize]);
  const handleOnPlacementLoad = useCallback(event => {
    setIsAdInfoVisible(true);
    if (!event.slot) return;
    track(receivedAdEvent({
      adUnitPath: event.slot.getAdUnitPath(),
      isMobileWeb: AdManager.getAdsPlatform() === AdPlatform.Mobile
    }));
    onLoad(event.slot.getAdUnitPath());
  }, [track, onLoad]);
  const registerPlacement = useCallback(() => {
    if (placementRegisteredRef.current) return;
    AdManager.registerPlacement({
      placementConfig: config,
      id,
      onPlacementLoad: handleOnPlacementLoad,
      onPlacementRequest: onRequest,
      onPlacementRenderEnded: handleOnPlacementRenderEnded,
      onImpressionViewable: handleOnImpressionViewable,
      isRefreshEnabled,
      isManuallyRendered,
      isManuallyRefreshed,
      iframeTitle: translate('advertisement')
    });
    placementRegisteredRef.current = true;
  }, [config, id, handleOnPlacementLoad, handleOnPlacementRenderEnded, handleOnImpressionViewable, isManuallyRendered, isManuallyRefreshed, isRefreshEnabled, translate, onRequest]);
  const handleAdRender = useCallback(isAdVisible => {
    onAdRender(isAdVisible);
    setIsAdInfoVisible(isAdVisible);
  }, [onAdRender]);
  useAdRenderedCheck({
    targetElement: adDivElement,
    onAdRender: handleAdRender
  });
  useEffect(() => {
    if (!adDivElement) return;
    const isConsentValuesLoaded = isCookieConsentVersion && isConsentBannerLoaded;

    if (isConsentValuesLoaded || !isCookieConsentVersion) {
      registerPlacement();
    }
  }, [adDivElement, registerPlacement, isCookieConsentVersion, isConsentBannerLoaded]);
  useEffect(() => {
    return () => {
      if (!placementRegisteredRef.current) return;
      AdManager.unregisterPlacement(id);
    };
  }, [id]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isAdInfoVisible && /*#__PURE__*/_jsx(AdInfo, {}), /*#__PURE__*/_jsx("div", {
      "data-testid": "rtb-advertisement",
      id: id,
      ref: setAdDivElement
    })]
  });
};

export default RtbAdvertisement;